<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import FupFlowsAPI from 'dashboard/api/fupFlows';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import {
  STEP_TYPES,
  CONDITION_ATTRIBUTES,
  CONDITION_OPERATORS,
  TRIGGER_TYPES,
  newStep,
  normalizeSteps,
  remapJumpTargets,
  withPositions,
  validateSteps,
  buildStepsAttributes,
  simulateFlow,
  unreachablePositions,
} from './fupFlowUtils';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { accountScopedRoute } = useAccount();

const isNew = computed(() => route.params.flowId === 'new');
const isFetching = ref(false);
const isSaving = ref(false);
const touched = ref(false);

const form = ref({
  name: '',
  active: true,
  trigger_type: 'no_reply',
  trigger_minutes: '60',
  trigger_stage: '',
  daily_cap: '3',
  window_start_hour: '',
  window_end_hour: '',
});

const steps = ref([]);
const removedStepIds = ref([]);
const simulation = ref(null);
const simulationStage = ref('');

const stepTypeOptions = computed(() =>
  STEP_TYPES.map(type => ({
    value: type,
    label: t(`FUP.BUILDER.STEP_${type.toUpperCase()}`),
  }))
);

const STEP_ICONS = {
  message: 'i-lucide-message-square',
  wait: 'i-lucide-clock-3',
  condition: 'i-lucide-git-branch',
  ai: 'i-lucide-sparkles',
  http: 'i-lucide-globe',
  stop: 'i-lucide-octagon',
};

const stepTypeIcon = stepType => STEP_ICONS[stepType] || 'i-lucide-circle';

const triggerOptions = computed(() =>
  TRIGGER_TYPES.map(type => ({
    value: type,
    label: t(`FUP.BUILDER.TRIGGER_${type.toUpperCase()}`),
  }))
);

const attributeOptions = computed(() =>
  CONDITION_ATTRIBUTES.map(attribute => ({
    value: attribute,
    label:
      attribute === 'pipeline_stage'
        ? t('FUP.BUILDER.ATTRIBUTE_PIPELINE_STAGE')
        : attribute,
  }))
);

const operatorOptions = computed(() =>
  CONDITION_OPERATORS.map(operator => ({
    value: operator,
    label: t(`FUP.BUILDER.OPERATOR_${operator.toUpperCase()}`),
  }))
);

const stepLabel = (step, index) => {
  const at = index + 1;
  const payload = step.payload || {};
  if (step.step_type === 'message') {
    return (
      payload.template_name ||
      (payload.body || '').slice(0, 40) ||
      `${t('FUP.BUILDER.STEP_MESSAGE')} ${at}`
    );
  }
  if (step.step_type === 'wait')
    return `${t('FUP.BUILDER.STEP_WAIT')}: ${payload.minutes || 0} min`;
  if (step.step_type === 'condition') {
    return `${t('FUP.BUILDER.STEP_CONDITION')}: ${payload.attribute || '?'} ${payload.operator || ''} ${payload.value || ''}`.trim();
  }
  return t(`FUP.BUILDER.STEP_${step.step_type.toUpperCase()}`);
};

const jumpOptions = computed(() =>
  steps.value.map((step, index) => ({
    value: String(index),
    label: `${index + 1}. ${stepLabel(step, index)}`,
  }))
);

const stepErrors = computed(() => {
  if (!touched.value) return {};
  const errors = {};
  validateSteps(withPositions(steps.value)).forEach(({ index, field }) => {
    errors[`${index}:${field}`] = true;
  });
  return errors;
});

const isValidHour = value => {
  if (String(value ?? '').trim() === '') return true;
  const hour = Number(value);
  return Number.isInteger(hour) && hour >= 0 && hour <= 23;
};

const formErrors = computed(() => {
  const errors = {};
  if (!form.value.name.trim()) errors.name = true;
  if (!isValidHour(form.value.window_start_hour))
    errors.window_start_hour = true;
  if (!isValidHour(form.value.window_end_hour)) errors.window_end_hour = true;
  if (
    (form.value.window_start_hour === '') !==
    (form.value.window_end_hour === '')
  ) {
    errors.window_start_hour = true;
    errors.window_end_hour = true;
  }
  if (!(Number(form.value.trigger_minutes) > 0)) errors.trigger_minutes = true;
  if (!(Number(form.value.daily_cap) > 0)) errors.daily_cap = true;
  if (
    form.value.trigger_type === 'stage_entry' &&
    !form.value.trigger_stage.trim()
  ) {
    errors.trigger_stage = true;
  }
  return errors;
});

const isValid = computed(
  () =>
    Object.keys(formErrors.value).length === 0 &&
    validateSteps(withPositions(steps.value)).length === 0
);

const fetchFlow = async () => {
  if (isNew.value) return;
  isFetching.value = true;
  try {
    const { data } = await FupFlowsAPI.getFlow(route.params.flowId);
    form.value = {
      name: data.name || '',
      active: data.active !== false,
      trigger_type: data.trigger_type || 'no_reply',
      trigger_minutes: String(data.trigger_minutes ?? 60),
      trigger_stage: data.trigger_stage || '',
      daily_cap: String(data.daily_cap ?? 3),
      window_start_hour:
        data.window_start_hour === null || data.window_start_hour === undefined
          ? ''
          : String(data.window_start_hour),
      window_end_hour:
        data.window_end_hour === null || data.window_end_hour === undefined
          ? ''
          : String(data.window_end_hour),
    };
    steps.value = normalizeSteps(data.steps || []);
  } catch {
    useAlert(t('FUP.LIST.LOAD_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

const addStep = stepType => {
  steps.value = withPositions([...steps.value, newStep(stepType)]);
  touched.value = true;
};

const removeStep = localId => {
  const target = steps.value.find(step => step.localId === localId);
  if (target?.id) removedStepIds.value.push(target.id);
  const beforeIds = steps.value.map(step => step.localId);
  steps.value = withPositions(
    remapJumpTargets(
      beforeIds,
      steps.value.filter(step => step.localId !== localId)
    )
  );
};

const orderSnapshot = ref([]);
const snapshotOrder = () => {
  orderSnapshot.value = steps.value.map(step => step.localId);
};

const onDragEnd = () => {
  steps.value = withPositions(
    remapJumpTargets(orderSnapshot.value, steps.value)
  );
  snapshotOrder();
};

const formatSimulationEntry = entry => {
  const typeLabel = t(`FUP.BUILDER.STEP_${entry.type.toUpperCase()}`);
  const detail = entry.detail || {};
  if (entry.type === 'message')
    return detail.text ? `${typeLabel}: ${detail.text}` : typeLabel;
  if (entry.type === 'wait') return `${typeLabel}: ${detail.minutes ?? 0} min`;
  if (entry.type === 'condition') {
    const attribute =
      detail.attribute === 'pipeline_stage'
        ? t('FUP.BUILDER.ATTRIBUTE_PIPELINE_STAGE')
        : detail.attribute || '?';
    const operator = detail.operator
      ? t(`FUP.BUILDER.OPERATOR_${detail.operator.toUpperCase()}`)
      : '';
    return `${typeLabel}: ${attribute} ${operator} ${detail.value ?? ''}`.trim();
  }
  if (entry.type === 'http')
    return detail.url ? `${typeLabel}: ${detail.url}` : typeLabel;
  return typeLabel;
};

const coerceOptionalHour = value => {
  if (String(value ?? '').trim() === '') return null;
  const hour = Number(value);
  return Number.isInteger(hour) && hour >= 0 && hour <= 23 ? hour : value;
};

const buildPayload = () => ({
  name: form.value.name.trim(),
  active: form.value.active,
  trigger_type: form.value.trigger_type,
  trigger_minutes: Number(form.value.trigger_minutes),
  trigger_stage:
    form.value.trigger_type === 'stage_entry'
      ? form.value.trigger_stage.trim()
      : null,
  daily_cap: Number(form.value.daily_cap),
  window_start_hour: coerceOptionalHour(form.value.window_start_hour),
  window_end_hour: coerceOptionalHour(form.value.window_end_hour),
  fup_steps_attributes: buildStepsAttributes(steps.value, removedStepIds.value),
});

const saveFlow = async () => {
  touched.value = true;
  if (!isValid.value) {
    useAlert(t('FUP.BUILDER.VALIDATION_ERROR'));
    return;
  }
  isSaving.value = true;
  try {
    const payload = buildPayload();
    if (isNew.value) {
      const { data } = await FupFlowsAPI.createFlow(payload);
      useAlert(t('FUP.BUILDER.SAVED'));
      router.push(accountScopedRoute('fup_flow_builder', { flowId: data.id }));
    } else {
      await FupFlowsAPI.updateFlow(route.params.flowId, payload);
      removedStepIds.value = [];
      useAlert(t('FUP.BUILDER.SAVED'));
      await fetchFlow();
    }
  } catch {
    useAlert(t('FUP.LIST.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const runSimulation = () => {
  const ordered = withPositions(steps.value);
  simulation.value = {
    ...simulateFlow(ordered, { stageValue: simulationStage.value }),
    unreachable: unreachablePositions(ordered),
  };
};

const goBack = () => {
  router.push(accountScopedRoute('fup_flows'));
};

onMounted(fetchFlow);
</script>

<template>
  <section class="flex flex-col flex-1 min-h-0 overflow-y-auto bg-n-background">
    <header
      class="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b border-n-weak"
    >
      <Button size="sm" color="slate" @click="goBack">
        {{ t('FUP.BUILDER.BACK') }}
      </Button>
      <h1 class="text-lg font-medium text-n-slate-12">
        {{ isNew ? t('FUP.BUILDER.NEW_TITLE') : form.name }}
      </h1>
      <span class="ms-auto" />
      <Button size="sm" :is-loading="isSaving" @click="saveFlow">
        {{ t('FUP.BUILDER.SAVE') }}
      </Button>
    </header>

    <div v-if="isFetching" class="flex items-center justify-center flex-1 p-8">
      <Spinner class="text-n-brand" />
    </div>

    <div
      v-else
      class="grid flex-1 grid-cols-1 gap-4 p-4 lg:grid-cols-[18rem_minmax(0,1fr)]"
    >
      <div class="flex flex-col gap-4 lg:sticky lg:top-4 lg:self-start">
        <div
          class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-1"
        >
          <Input
            v-model="form.name"
            :label="t('FUP.BUILDER.NAME')"
            :placeholder="t('FUP.BUILDER.NAME_PLACEHOLDER')"
            :message-type="touched && formErrors.name ? 'error' : 'info'"
          />
          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            <Switch v-model="form.active" />
            {{ form.active ? t('FUP.LIST.ACTIVE') : t('FUP.LIST.PAUSED') }}
          </label>
          <label class="flex flex-col gap-1.5 text-sm text-n-slate-12">
            {{ t('FUP.BUILDER.TRIGGER') }}
            <Select v-model="form.trigger_type" :options="triggerOptions" />
          </label>
          <Input
            v-model="form.trigger_minutes"
            :label="t('FUP.BUILDER.TRIGGER_MINUTES')"
            :message-type="
              touched && formErrors.trigger_minutes ? 'error' : 'info'
            "
          />
          <Input
            v-if="form.trigger_type === 'stage_entry'"
            v-model="form.trigger_stage"
            :label="t('FUP.BUILDER.TRIGGER_STAGE_VALUE')"
            :message-type="
              touched && formErrors.trigger_stage ? 'error' : 'info'
            "
          />
          <Input
            v-model="form.daily_cap"
            :label="t('FUP.BUILDER.DAILY_CAP')"
            :message-type="touched && formErrors.daily_cap ? 'error' : 'info'"
          />
          <p class="text-sm text-n-slate-12">{{ t('FUP.BUILDER.WINDOW') }}</p>
          <div class="grid grid-cols-2 gap-2">
            <Input
              v-model="form.window_start_hour"
              :label="t('FUP.BUILDER.WINDOW_FROM')"
            />
            <Input
              v-model="form.window_end_hour"
              :label="t('FUP.BUILDER.WINDOW_TO')"
            />
          </div>
        </div>

        <div
          class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-1"
        >
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('FUP.BUILDER.SIMULATE') }}
          </h2>
          <Input
            v-model="simulationStage"
            :label="t('FUP.BUILDER.SIMULATE_STAGE')"
          />
          <Button size="sm" color="slate" @click="runSimulation">
            {{ t('FUP.BUILDER.SIMULATE_RUN') }}
          </Button>
          <p v-if="!simulation" class="text-xs text-n-slate-11">
            {{ t('FUP.BUILDER.SIMULATE_EMPTY') }}
          </p>
          <ol v-else class="flex flex-col gap-1.5">
            <li
              v-for="(entry, entryIndex) in simulation.entries"
              :key="`${entry.position}-${entry.type}-${entryIndex}`"
              class="px-2 py-1.5 text-xs rounded-lg bg-n-alpha-2 text-n-slate-12"
            >
              <span class="font-medium">{{ entry.position + 1 }}.</span>
              {{ formatSimulationEntry(entry) }}
              <span v-if="entry.branch" class="font-medium text-n-brand">
                ({{
                  t(
                    `FUP.BUILDER.SIMULATE_BRANCH_${entry.branch.toUpperCase()}`
                  )
                }})
              </span>
            </li>
          </ol>
          <p
            v-for="warning in simulation?.warnings || []"
            :key="`${warning.position}-${warning.code}`"
            class="text-xs text-n-ruby-11"
          >
            {{
              t(
                warning.code === 'loop'
                  ? 'FUP.BUILDER.SIMULATE_WARNING_LOOP'
                  : 'FUP.BUILDER.SIMULATE_WARNING_BAD_JUMP'
              )
            }}
          </p>
          <p
            v-if="simulation && simulation.unreachable.length"
            class="text-xs text-n-amber-11"
          >
            {{ t('FUP.BUILDER.UNREACHABLE_WARNING') }}
          </p>
        </div>
      </div>

      <div class="flex flex-col gap-3 min-w-0">
        <div
          class="flex flex-wrap items-center gap-2 p-3 border rounded-xl border-n-weak bg-n-solid-1"
        >
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('FUP.BUILDER.PALETTE') }}
          </span>
          <Button
            v-for="option in stepTypeOptions"
            :key="option.value"
            size="sm"
            color="slate"
            :icon="stepTypeIcon(option.value)"
            @click="addStep(option.value)"
          >
            {{ option.label }}
          </Button>
        </div>

        <div
          v-if="!steps.length"
          class="flex flex-col items-center justify-center gap-2 p-10 text-center border border-dashed rounded-xl border-n-weak bg-n-alpha-black1"
        >
          <span class="i-lucide-workflow text-3xl text-n-slate-9" />
          <p class="mb-0 text-sm text-n-slate-11">
            {{ t('FUP.LIST.EMPTY_DESCRIPTION') }}
          </p>
        </div>

        <Draggable
          v-if="steps.length"
          :list="steps"
          item-key="localId"
          handle=".fup-drag-handle"
          ghost-class="opacity-40"
          class="flex flex-col gap-3 p-3 border border-dashed rounded-xl border-n-weak bg-n-alpha-black1"
          @change="onDragEnd"
          @start="snapshotOrder"
        >
          <template #item="{ element: step, index }">
            <div class="relative flex gap-3">
              <div class="flex flex-col items-center pt-3">
                <span
                  class="grid flex-shrink-0 rounded-full place-content-center size-8 bg-n-alpha-3"
                >
                  <span
                    class="size-4 text-n-brand"
                    :class="stepTypeIcon(step.step_type)"
                  />
                </span>
                <span
                  v-if="index < steps.length - 1"
                  class="flex-1 w-px my-2 bg-n-weak"
                />
              </div>

              <div
                class="flex-1 min-w-0 p-4 border rounded-xl border-n-weak bg-n-solid-1"
                :class="{
                  'border-n-ruby-9': stepErrors[`${index}:payload`],
                }"
              >
                <div class="flex items-center gap-2 mb-3">
                  <span
                    class="fup-drag-handle i-lucide-grip-vertical cursor-grab text-n-slate-10"
                  />
                  <span
                    class="flex items-center justify-center rounded-md size-6 bg-n-alpha-3 text-xs font-medium text-n-slate-12"
                  >
                    {{ index + 1 }}
                  </span>
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ stepLabel(step, index) }}
                  </span>
                  <span class="ms-auto" />
                  <Button
                    size="xs"
                    color="slate"
                    @click="removeStep(step.localId)"
                  >
                    {{ t('FUP.BUILDER.DELETE_STEP') }}
                  </Button>
                </div>

                <div
                  v-if="step.step_type === 'message'"
                  class="flex flex-col gap-2"
                >
                  <TextArea
                    v-model="step.payload.body"
                    :label="t('FUP.BUILDER.BODY')"
                    :placeholder="t('FUP.BUILDER.BODY_PLACEHOLDER')"
                    auto-height
                  />
                  <Input
                    v-model="step.payload.template_name"
                    :label="t('FUP.BUILDER.TEMPLATE_NAME')"
                  />
                  <Input
                    v-model="step.payload.template_language"
                    :label="t('FUP.BUILDER.TEMPLATE_LANGUAGE')"
                  />
                </div>

                <div v-else-if="step.step_type === 'wait'">
                  <Input
                    v-model="step.payload.minutes"
                    :label="t('FUP.BUILDER.MINUTES')"
                  />
                </div>

                <div
                  v-else-if="step.step_type === 'condition'"
                  class="flex flex-col gap-2"
                >
                  <label class="flex flex-col gap-1.5 text-sm text-n-slate-12">
                    {{ t('FUP.BUILDER.ATTRIBUTE') }}
                    <Select
                      v-model="step.payload.attribute"
                      :options="attributeOptions"
                    />
                  </label>
                  <label class="flex flex-col gap-1.5 text-sm text-n-slate-12">
                    {{ t('FUP.BUILDER.OPERATOR') }}
                    <Select
                      v-model="step.payload.operator"
                      :options="operatorOptions"
                    />
                  </label>
                  <Input
                    v-model="step.payload.value"
                    :label="t('FUP.BUILDER.VALUE')"
                  />
                  <label class="flex flex-col gap-1.5 text-sm text-n-slate-12">
                    {{ t('FUP.BUILDER.THEN_GO_TO') }}
                    <Select
                      v-model="step.payload.then_position"
                      :options="jumpOptions"
                    />
                  </label>
                  <p class="text-xs text-n-slate-11">
                    {{ t('FUP.BUILDER.ELSE_FALLS_THROUGH') }}
                  </p>
                </div>

                <p
                  v-else-if="step.step_type === 'ai'"
                  class="text-xs text-n-slate-11"
                >
                  {{ t('FUP.BUILDER.AI_NOTE') }}
                </p>

                <div v-else-if="step.step_type === 'http'">
                  <Input
                    v-model="step.payload.url"
                    :label="t('FUP.BUILDER.URL')"
                  />
                </div>

                <p v-else class="text-xs text-n-slate-11">
                  {{ t('FUP.BUILDER.STOP_NOTE') }}
                </p>
              </div>
            </div>
          </template>
        </Draggable>
      </div>
    </div>
  </section>
</template>
